import React, { useEffect, useLayoutEffect, useMemo, useState, useCallback, useRef } from 'react';
import { debounce } from 'lodash-es';

export type IsTruncatedOptions = {
  /**
   * Disable automatic recalculation on window resize events.
   * Set to true for better performance when using many hook instances.
   */
  ignoreWindowResize?: boolean;
  /**
   * Disable automatic recalculation on orientation change events.
   * Note: orientationchange event is only available on mobile browsers.
   */
  ignoreOrientationChange?: boolean;
  /**
   * Custom function to get the target element for truncation checking.
   * Useful when the element to check differs from the ref element.
   * @param refNode The element from the ref
   * @returns The target element to check, or null if not found
   * @example (el) => el.firstElementChild
   */
  getTargetNode?: (refNode: Element) => Element | null;
};

const DEBOUNCE_DELAY_MS = 300;

const defaultGetTargetNode: IsTruncatedOptions['getTargetNode'] = refNode => refNode;

export function isElementTruncated(el: Element): boolean {
  if (!el) return false;
  const isHorizontallyTruncated = el.scrollWidth > el.clientWidth;
  return isHorizontallyTruncated;
}

function checkIfElementTruncated(
  ref: React.RefObject<Element | null>,
  getTargetNode: IsTruncatedOptions['getTargetNode'] = defaultGetTargetNode,
): boolean {
  if (ref.current === null) {
    return false;
  }
  const targetNode = getTargetNode(ref.current);
  if (targetNode === null) {
    return false;
  }
  return isElementTruncated(targetNode);
}

export function useIsTruncated<T extends Element>(
  ref: React.RefObject<null | T>,
  options: IsTruncatedOptions = {},
): boolean {
  const [isTruncated, setIsTruncated] = useState(false);
  const previousIsTruncated = useRef<boolean>(false);
  const getTargetNode = typeof options.getTargetNode === 'function' ? options.getTargetNode : defaultGetTargetNode;

  const checkTruncation = useCallback(() => {
    const currentIsTruncated = checkIfElementTruncated(ref, getTargetNode);

    // Only update state if value actually changed to prevent unnecessary re-renders
    if (currentIsTruncated !== previousIsTruncated.current) {
      previousIsTruncated.current = currentIsTruncated;
      setIsTruncated(currentIsTruncated);
    }
  }, [ref, getTargetNode]);

  useLayoutEffect(() => {
    checkTruncation();
  });

  const debouncedResizeCallback = useMemo(() => debounce(checkTruncation, DEBOUNCE_DELAY_MS), [checkTruncation]);

  useEffect(() => {
    const { ignoreWindowResize, ignoreOrientationChange } = options;
    const shouldListenWindowResize = !ignoreWindowResize;
    const shouldListenOrientationChange = !shouldListenWindowResize && !ignoreOrientationChange;

    if (shouldListenWindowResize) {
      window.addEventListener('resize', debouncedResizeCallback);
    }
    if (shouldListenOrientationChange) {
      window.addEventListener('orientationchange', debouncedResizeCallback);
    }

    return () => {
      if (shouldListenWindowResize) {
        window.removeEventListener('resize', debouncedResizeCallback);
      }
      if (shouldListenOrientationChange) {
        window.removeEventListener('orientationchange', debouncedResizeCallback);
      }
      debouncedResizeCallback.cancel();
    };
  }, [debouncedResizeCallback, options]);

  return isTruncated;
}
